import { useForm, useFieldArray } from 'react-hook-form';

import {
  TrackableItem,
  TrackableObjectType,
  UpdateTrackableItem,
} from 'types/trackables';

import { useUpdateTrackableItem } from '../hooks/useUpdateTrackableItem';
import { TRACKABLE_OBJECT_TYPE_LABELS } from '../utils';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';

interface FormData extends UpdateTrackableItem {
  licensePlate: string;
  customAttributes?: Array<{ key: string; value: string }>;
}

export interface EditTrackableItemFormProps {
  trackableItem: TrackableItem;
  trackableListId: string;
  objectType: TrackableObjectType;
  onSuccess?: () => void;
}

export function EditTrackableItemForm({
  trackableItem,
  trackableListId,
  objectType,
  onSuccess,
}: EditTrackableItemFormProps) {
  const itemType = TRACKABLE_OBJECT_TYPE_LABELS[objectType].toLowerCase();

  // Process existing attributes for form
  const getInitialCustomAttributes = () => {
    if (objectType !== 'custom' || !trackableItem.attributes)
      return [{ key: '', value: '' }];

    const attrs = Object.entries(trackableItem.attributes).map(
      ([key, value]) => ({
        key,
        value: String(value),
      })
    );

    return attrs.length > 0 ? attrs : [{ key: '', value: '' }];
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
  } = useForm<FormData>({
    defaultValues: {
      name: trackableItem.name,
      description: trackableItem.description || '',
      labels: Array.isArray(trackableItem.labels) ? trackableItem.labels : [],
      licensePlate: trackableItem.attributes?.licensePlate || '',
      customAttributes: getInitialCustomAttributes(),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customAttributes',
  });

  const {
    mutate: updateTrackableItem,
    isLoading,
    isSuccess,
  } = useUpdateTrackableItem({
    onSuccess: () => {
      onSuccess?.();
    },
  });

  const onSubmit = (data: FormData) => {
    const processedData: UpdateTrackableItem = {
      name: data.name,
      description: data.description,
      labels:
        typeof data.labels === 'string'
          ? (data.labels as string)
              .split(',')
              .map((label: string) => label.trim())
              .filter(Boolean)
          : data.labels || [],
      attributes: {},
    };

    // Handle vehicle license plate
    if (objectType === 'vehicle') {
      processedData.attributes = {
        ...processedData.attributes,
        licensePlate: data.licensePlate?.trim() || '',
      };
    }

    // Handle custom attributes
    if (objectType === 'custom' && data.customAttributes) {
      const customAttrs = data.customAttributes
        .filter((attr) => attr.key.trim() && attr.value.trim())
        .reduce(
          (acc, attr) => {
            acc[attr.key.trim()] = attr.value.trim();
            return acc;
          },
          {} as Record<string, string>
        );

      processedData.attributes = {
        ...processedData.attributes,
        ...customAttrs,
      };
    }

    updateTrackableItem({
      trackable_list_id: trackableListId,
      trackable_item_id: trackableItem.id,
      data: processedData,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex h-full flex-col">
      <div className="flex-shrink-0 space-y-4">
        <Field label="Name" required error={errors.name?.message}>
          <Input
            {...register('name', {
              required: `Please enter a name for the ${itemType}.`,
            })}
            placeholder={`Enter ${itemType} name...`}
          />
        </Field>

        <Field label="Description">
          <Input
            {...register('description')}
            placeholder="Enter description (optional)..."
          />
        </Field>

        <Field label="Labels">
          <Input
            {...register('labels')}
            placeholder="Enter labels separated by commas (optional)..."
          />
        </Field>

        {objectType === 'vehicle' && (
          <Field
            label="License Plate"
            required
            error={errors.licensePlate?.message}
          >
            <Input
              {...register('licensePlate', {
                required: 'Please enter a license plate number.',
              })}
              placeholder="Enter license plate number..."
            />
          </Field>
        )}
      </div>

      {objectType === 'custom' && (
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <div className="mb-3 flex flex-shrink-0 items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Custom Attributes
            </label>
            <Button
              type="button"
              variant="secondary"
              onClick={() => append({ key: '', value: '' })}
            >
              Add Attribute
            </Button>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto pr-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-start gap-2">
                <div className="flex-1">
                  <Input
                    {...register(`customAttributes.${index}.key` as const)}
                    placeholder="Key"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    {...register(`customAttributes.${index}.value` as const)}
                    placeholder="Value"
                  />
                </div>
                {fields.length > 1 && (
                  <IconButton
                    icon="delete"
                    label="Remove attribute"
                    variant="ghost"
                    onClick={() => remove(index)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex-shrink-0">
        <ButtonGroup>
          <Button type="submit" variant="primary" loading={isLoading}>
            Save Changes
          </Button>

          {isDirty && <InlineNotification>Unsaved changes</InlineNotification>}

          {isSuccess && !isDirty && (
            <InlineNotification intent="success">Saved!</InlineNotification>
          )}
        </ButtonGroup>
      </div>
    </form>
  );
}
